import { useContext, useEffect, useRef } from 'react';
import * as L from 'leaflet';
import 'leaflet-draw';
import '../../../../../dist/azure-maps-leaflet';
import { Api } from '@services';
import { useMapStore } from '../MapStore/UseMapStore';
import {
  createClusterLayer,
  translateCoords,
  updateImageOpacity,
  updateTileLayer,
} from '../MapFunctions/MapHelperFunctions';
import { getMapIcon } from '../MapComponents/MapMiscComponents/MapIcons';
import { ThemeContext } from '@context/ThemeContext';

// Helper to determine Azure Maps domain directly from host context
const getAzureMapsDomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  const isGov =
    hostname.endsWith('.gov') || hostname.includes('azure-gov') || hostname.includes('usgov');
  return isGov ? 'atlas.azure.us' : 'atlas.microsoft.com';
};

const getTilesetId = (theme: string) =>
  theme === 'dark' ? 'microsoft.base.darkgrey' : 'microsoft.base.road';

const clearedMapRefs = {
  mapInstance: null,
  azureLayer: null,
  boundsLayer: null,
  imageLayer: null,
  tempLayer: null,
  placeLayer: null,
  gatewayLayer: null,
  stationaryLayer: null,
  assetLayer: null,
  placeDrawLayer: null,
  gatewayDrawLayer: null,
  stationaryDrawLayer: null,
  scaleDrawLayer: null,
};

export function useInitializeMap() {
  const mapRef = useRef<L.Map | null>(null);
  const initializingRef = useRef(false);
  const unmountedRef = useRef(false);

  const mapImg = useMapStore((s) => s.mapImg);
  const isStatic = useMapStore((s) => s.isStatic);
  const overlayFC = useMapStore((s) => s.overlayFC);
  const azureLayer = useMapStore((s) => s.azureLayer);

  const { theme } = useContext(ThemeContext);

  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    if (!azureLayer) return;
    updateTileLayer(getTilesetId(theme), azureLayer);
  }, [theme, azureLayer]);

  useEffect(() => {
    unmountedRef.current = false;

    return () => {
      unmountedRef.current = true;
      initializingRef.current = false;

      if (mapRef.current) {
        mapRef.current.off();
        mapRef.current.remove();
        mapRef.current = null;
      }

      useMapStore.setState(clearedMapRefs as any);
    };
  }, []);

  useEffect(() => {
    if (mapRef.current || initializingRef.current) return;
    if (!document.getElementById('myMap')) return;
    if (!overlayFC?.features?.length || !mapImg) return;

    initializingRef.current = true;

    const initMapWithAzure = async () => {
      let mapKey = '';

      if (!isStatic) {
        try {
          const resp = await Api.get('/me/map-server-key');
          mapKey = resp.data as string;
        } catch (err) {
          console.error('Failed to fetch map key:', err);
        }
      }

      if (unmountedRef.current || mapRef.current || !document.getElementById('myMap')) {
        initializingRef.current = false;
        return;
      }

      const {
        overlayFC: fc,
        mapImg: img,
        opacity,
        markerSize,
        isStatic: staticNow,
        setAzureLayer,
        setBounds,
        setIsDirty,
        setDrawEvent,
        setVertexEvent,
        addTempObject,
        updateObject,
      } = useMapStore.getState() as any;

      if (!fc?.features?.length || !img) {
        initializingRef.current = false;
        return;
      }

      const map = L.map('myMap', {
        attributionControl: false,
        maxBoundsViscosity: 1.0,
        maxZoom: 25,
      });
      mapRef.current = map;

      // Native Azure Maps layer
      let azure: any = null;
      if (!staticNow && mapKey) {
        azure = (L.tileLayer as any)
          .azureMaps({
            authOptions: {
              authType: 'subscriptionKey',
              subscriptionKey: mapKey,
              azMapsDomain: getAzureMapsDomain(),
            },
            tilesetId: getTilesetId(themeRef.current),
            maxNativeZoom: 18,
            maxZoom: 25,
            minZoom: 2,
            crossOrigin: 'anonymous',
          })
          .addTo(map);

        setAzureLayer(azure);
      }

      const boundsFeature = fc.features[0];
      const boundsCoords = boundsFeature.geometry.coordinates[0];

      // Bounds GeoJSON
      const boundsGeo = L.geoJSON(
        boundsFeature as any,
        {
          style: { color: 'var(--primary)', fillColor: '#ffffff00', weight: 3, dashArray: '0, 0' },
          draggable: true as any,
          transform: true as any,
        } as any,
      );

      // Image overlay
      const imageOverlay = (L as any).imageOverlay.rotated(
        img,
        translateCoords(3, boundsCoords),
        translateCoords(2, boundsCoords),
        translateCoords(0, boundsCoords),
      );

      // Layers
      const temp = new L.FeatureGroup();
      const places = L.featureGroup();
      const gateways = createClusterLayer('gateway');
      const stationary = createClusterLayer('stationary-beacon');
      const assets = createClusterLayer('asset');

      [boundsGeo, imageOverlay, temp, places, gateways, stationary, assets].forEach((layer) => {
        if (layer) map.addLayer(layer);
      });

      if (map.hasLayer(imageOverlay)) {
        updateImageOpacity(opacity, imageOverlay);
      }

      // Fit bounds safely
      const mapBounds = boundsGeo?.getBounds();
      if (mapBounds && mapBounds.isValid()) {
        setBounds(mapBounds);
        map.setMaxBounds(L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180)));
        map.fitBounds(mapBounds);
      }

      // Draw tools
      const placeDraw = new (L as any).Draw.Polygon(map, {
        shapeOptions: { color: 'var(--c-primary)', fillColor: '#ffffff00', weight: 2, opacity: 1 },
      });
      const gatewayDraw = new (L as any).Draw.Marker(map, {
        icon: getMapIcon('add', '--nav-text', true, markerSize),
      });
      const stationaryDraw = new (L as any).Draw.Marker(map, {
        icon: getMapIcon('add', '--nav-text', true, markerSize),
      });
      const scaleDraw = new (L as any).Draw.Polyline(map, {
        shapeOptions: { color: 'var(--c-primary)', fillColor: '#ffffff00', weight: 2, opacity: 1 },
      });

      // Draw events
      map.on((L as any).Draw.Event.CREATED, (e: any) => {
        setDrawEvent(e);
        addTempObject(e);
        setIsDirty(true);
      });
      map.on((L as any).Draw.Event.DRAWVERTEX, (e: any) => {
        setVertexEvent(e);
        setIsDirty(true);
      });
      map.on((L as any).Draw.Event.EDITMOVE, (e: any) => {
        updateObject(e.layer);
        setIsDirty(true);
      });
      map.on((L as any).Draw.Event.EDITVERTEX, (e: any) => {
        updateObject(e.poly);
        setIsDirty(true);
      });

      // Publish to Zustand
      useMapStore.setState({
        mapInstance: map,
        boundsLayer: boundsGeo,
        imageLayer: imageOverlay,
        tempLayer: temp,
        placeLayer: places,
        gatewayLayer: gateways,
        stationaryLayer: stationary,
        assetLayer: assets,
        placeDrawLayer: placeDraw,
        gatewayDrawLayer: gatewayDraw,
        stationaryDrawLayer: stationaryDraw,
        scaleDrawLayer: scaleDraw,
      } as any);

      initializingRef.current = false;
    };

    initMapWithAzure().catch((err) => {
      console.error('Map initialization failed:', err);
      initializingRef.current = false;
    });
  }, [overlayFC, mapImg, isStatic]);

  return { mapRef };
}
